import { useEffect, useState } from "react";
import { Loader2, Baby, CalendarHeart, Save } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const STATUS_STYLES = {
    done: "bg-emerald-50 text-emerald-700 border-emerald-200",
    due: "bg-amber-50 text-amber-700 border-amber-300",
    overdue: "bg-red-50 text-red-700 border-red-300",
    upcoming: "bg-slate-50 text-slate-500 border-slate-200",
};

function MiniChart({ points, label, unit, color = "#E06D1F" }) {
    const nums = points.filter((p) => !isNaN(parseFloat(p.y)));
    if (nums.length < 2) return <p className="text-xs text-slate-400 py-4" data-testid={`chart-empty-${label}`}>Need 2+ visits with {label.toLowerCase()} to plot trend</p>;
    const w = 280, h = 80, pad = 8;
    const ys = nums.map((p) => parseFloat(p.y));
    const min = Math.min(...ys), max = Math.max(...ys);
    const range = max - min || 1;
    const pts = nums.map((p, i) => `${pad + (i * (w - 2 * pad)) / (nums.length - 1)},${h - pad - ((parseFloat(p.y) - min) / range) * (h - 2 * pad)}`).join(" ");
    return (
        <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`chart-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <p className="text-[11px] font-medium uppercase tracking-wider text-slate-400 mb-1">{label} trend {unit && `(${unit})`}</p>
            <svg viewBox={`0 0 ${w} ${h}`} className="w-full">
                <polyline points={pts} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                {nums.map((p, i) => (
                    <circle key={i} cx={pad + (i * (w - 2 * pad)) / (nums.length - 1)} cy={h - pad - ((parseFloat(p.y) - min) / range) * (h - 2 * pad)} r="3" fill={color} />
                ))}
            </svg>
            <p className="text-[10px] text-slate-400 mt-1">{nums[0].x} → {nums[nums.length - 1].x} · range {min}–{max} {unit}</p>
        </div>
    );
}

export default function PregnancyTab({ patientId, patient, onUpdate }) {
    const [anc, setAnc] = useState(null);
    const [flow, setFlow] = useState(null);
    const [lmp, setLmp] = useState("");
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const [{ data: a }, { data: f }] = await Promise.all([
                api.get(`/patients/${patientId}/anc`),
                api.get(`/patients/${patientId}/anc-flowsheet`),
            ]);
            setAnc(a);
            setFlow(f);
        } catch (e) { toast.error(fmtErr(e)); }
    };

    useEffect(() => { load(); }, [patientId]);

    const saveLmp = async () => {
        if (!lmp) return;
        setSaving(true);
        try {
            const { data: p } = await api.get(`/patients/${patientId}`);
            await api.put(`/patients/${patientId}`, { ...p, lmp });
            toast.success("LMP saved — EDD auto-computed");
            setLmp("");
            load();
            onUpdate && onUpdate();
        } catch (e) { toast.error(fmtErr(e)); } finally { setSaving(false); }
    };

    if (!anc || !flow) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#E06D1F]" /></div>;

    if (!anc.pregnant) {
        return (
            <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm max-w-lg" data-testid="anc-setup-card">
                <Baby className="h-8 w-8 text-[#E06D1F]" />
                <h3 className="mt-3 font-heading font-semibold text-slate-900">No active pregnancy record</h3>
                <p className="mt-1 text-sm text-slate-500">Enter the Last Menstrual Period (LMP) to activate EDD calculation, gestational age tracking, and trimester checklists.</p>
                <div className="mt-4 flex gap-2 items-end">
                    <div className="flex-1">
                        <label className="text-xs font-medium text-slate-600">LMP Date</label>
                        <Input data-testid="anc-lmp-input" type="date" value={lmp} onChange={(e) => setLmp(e.target.value)} max={new Date().toISOString().slice(0, 10)} />
                    </div>
                    <Button data-testid="anc-lmp-save-btn" onClick={saveLmp} disabled={saving || !lmp} className="bg-[#E06D1F] hover:bg-[#B45309] text-white gap-1.5">
                        {saving && <Loader2 className="h-4 w-4 animate-spin" />} Set LMP
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="pregnancy-tab">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="rounded-xl border border-orange-200 bg-[#FFF4EC] p-5" data-testid="anc-ga-card">
                    <p className="text-[11px] font-medium uppercase tracking-wider text-[#C2410C]">Gestational Age</p>
                    <p className="font-heading text-3xl font-extrabold text-slate-900 mt-1">{anc.ga_weeks}<span className="text-lg font-bold">w</span> {anc.ga_days}<span className="text-lg font-bold">d</span></p>
                    <p className="text-xs text-slate-500 mt-1">Trimester {anc.trimester}{anc.gravida ? ` · G${anc.gravida.replace(/^G/i, "")}` : ""}{anc.para ? ` P${anc.para.replace(/^P/i, "")}` : ""}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="anc-edd-card">
                    <p className="text-[11px] font-medium uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><CalendarHeart className="h-3.5 w-3.5 text-[#E06D1F]" />EDD (LMP + 280 days)</p>
                    <p className="font-heading text-2xl font-extrabold text-slate-900 mt-1">{anc.edd}</p>
                    <p className="text-xs text-slate-500 mt-1">LMP: {anc.lmp}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="anc-checklist-summary">
                    <p className="text-[11px] font-medium uppercase tracking-wider text-slate-400">Screening Status</p>
                    <p className="font-heading text-2xl font-extrabold text-slate-900 mt-1">{anc.checklist.filter((c) => c.status === "done").length}/{anc.checklist.length} done</p>
                    <p className="text-xs text-red-600 mt-1">{anc.checklist.filter((c) => c.status === "overdue").length} overdue · {anc.checklist.filter((c) => c.status === "due").length} due now</p>
                </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="anc-checklist-card">
                <h3 className="font-heading font-semibold text-slate-900 mb-3">Trimester Screening Checklist</h3>
                <div className="flex flex-wrap gap-2">
                    {anc.checklist.map((c) => (
                        <span key={c.key} data-testid={`checklist-${c.key}`} className={`rounded-full border px-3 py-1.5 text-xs font-medium ${STATUS_STYLES[c.status]}`}>
                            {c.name} <span className="opacity-70">({c.window})</span> — {c.status === "done" ? "Done" : c.status === "due" ? "Due now" : c.status === "overdue" ? "Overdue" : "Upcoming"}
                        </span>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <MiniChart label="Weight" unit="kg" points={flow.map((r) => ({ x: `${r.date} (${r.ga})`, y: r.weight }))} />
                <MiniChart label="BP Systolic" unit="mmHg" points={flow.map((r) => ({ x: `${r.date} (${r.ga})`, y: (r.bp || "").split("/")[0] }))} color="#C2410C" />
            </div>

            <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="anc-flowsheet-card">
                <div className="px-5 py-4 border-b border-slate-100"><h3 className="font-heading font-semibold text-slate-900">ANC Flowsheet</h3></div>
                {flow.length === 0 ? (
                    <p className="px-5 py-8 text-sm text-slate-500">No ANC data yet — record fundal height, FHR and movement in the visit vitals.</p>
                ) : (
                    <table className="w-full text-sm">
                        <thead><tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-600">
                            <th className="px-4 py-2">Date</th><th className="px-4 py-2">GA</th><th className="px-4 py-2">BP</th><th className="px-4 py-2">Weight</th><th className="px-4 py-2">Fundal Ht</th><th className="px-4 py-2">FHR</th><th className="px-4 py-2">Movement</th><th className="px-4 py-2">Presentation</th>
                        </tr></thead>
                        <tbody>
                            {flow.map((r) => (
                                <tr key={r.visit_id} data-testid={`flowsheet-row-${r.visit_id}`} className="border-t border-slate-100">
                                    <td className="px-4 py-2 text-slate-700">{r.date}</td>
                                    <td className="px-4 py-2 font-semibold text-[#C2410C]">{r.ga}</td>
                                    <td className="px-4 py-2">{r.bp || "—"}</td>
                                    <td className="px-4 py-2">{r.weight ? `${r.weight} kg` : "—"}</td>
                                    <td className="px-4 py-2">{r.fundal_height ? `${r.fundal_height} cm` : "—"}</td>
                                    <td className="px-4 py-2">{r.fhr ? `${r.fhr}/min` : "—"}</td>
                                    <td className="px-4 py-2">{r.fetal_movement || "—"}</td>
                                    <td className="px-4 py-2">{r.presentation || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
